import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import "./App.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "animate.css";

// Context providers
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider } from "./context/ThemeContext"; // This is our custom ThemeProvider
import { SidenavProvider, useSidenav } from "./context/SidenavContext";
import { useTheme } from "@mui/material/styles"; // Import MUI's useTheme hook

// Components
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";

// Pages
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ExcavatorList from "./pages/ExcavatorList";
import ExcavatorDetail from "./pages/ExcavatorDetail";
import ExcavatorAdd from "./pages/ExcavatorAdd";
import ExcavatorEdit from "./pages/ExcavatorEdit";
import ReportList from "./pages/ReportList";
import ReportDetail from "./pages/ReportDetail";
import CreateReport from "./pages/CreateReport";
import UserManagement from "./pages/UserManagement";
import Profile from "./pages/Profile";
import NotFound from "./pages/NotFound";
import ReportStats from "./pages/ReportStats";
import BetriebstageBuchList from "./pages/BetriebstageBuchList";
import BetriebstageBuchDetail from "./pages/BetriebstageBuchDetail";
import BetriebstageBuchForm from "./pages/BetriebstageBuchForm";
import BatchBetriebstagebuchForm from "./pages/BatchBetriebstagebuchForm";

import Inventory from './pages/Inventory';

// Layout wrapper component to conditionally render Navigation and Footer
const AppLayout = ({ children }) => {
  const location = useLocation();
  const authPages = ["/login", "/register"];
  const hideNavigation = authPages.includes(location.pathname);
  const { collapsed } = useSidenav();
  const muiTheme = useTheme(); // Correctly use MUI's useTheme here

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Drawer widths
  const drawerWidth = 240;
  const miniDrawerWidth = 65; // Matched with Navigation.js

  return (
    <div className={`app-container${!hideNavigation ? " sidenav-layout" : ""}`}>
      {!hideNavigation && <Navigation />}
      <main
        className={hideNavigation ? "auth-page" : "page-content"}
        style={{
          flexGrow: 1,
          backgroundColor: muiTheme.palette.background.default,
          // Apply padding only if navigation is shown; auth pages might handle their own.
          padding: hideNavigation ? 0 : muiTheme.spacing(3),
          ...(!hideNavigation && { // Styles for when navigation is shown
            marginLeft:
              typeof window !== "undefined" && window.innerWidth >= muiTheme.breakpoints.values.md
                ? collapsed
                  ? miniDrawerWidth
                  : drawerWidth
                : 0,
            transition: muiTheme.transitions.create('margin', {
              easing: muiTheme.transitions.easing.sharp,
              duration: collapsed
                ? muiTheme.transitions.duration.leavingScreen
                : muiTheme.transitions.duration.enteringScreen,
            }),
          }),
          ...(hideNavigation && { // Specific styles for auth pages (Login, Register)
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh', // Ensure auth pages take full viewport height
          })
        }}
      >
        {children}
      </main>
      {/* Footer can also be styled using theme if needed, or its own styles */}
      {!hideNavigation && <Footer />}
    </div>
  );
};

function App() {
  return (
    <ThemeProvider> {/* Our custom ThemeProvider that includes MuiThemeProvider */}
      <AuthProvider>
        <LanguageProvider>
          <SidenavProvider>
            <Router>
              <Routes>
                {/* Public routes */}
                <Route
                  path="/login"
                  element={
                    <AppLayout>
                      <Login />
                    </AppLayout>
                  }
                />
                <Route
                  path="/register"
                  element={
                    <AppLayout>
                      <Register />
                    </AppLayout>
                  }
                />

                {/* Protected routes */}
                <Route
                  path="/"
                  element={
                    <AppLayout>
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <AppLayout>
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/excavators"
                  element={
                    <AppLayout>
                      <ProtectedRoute>
                        <ExcavatorList />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/excavators/add"
                  element={
                    <AppLayout>
                      <ProtectedRoute managerOnly={true}>
                        <ExcavatorAdd />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/excavators/edit/:id"
                  element={
                    <AppLayout>
                      <ProtectedRoute managerOnly={true}>
                        <ExcavatorEdit />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/excavators/:id"
                  element={
                    <AppLayout>
                      <ProtectedRoute>
                        <ExcavatorDetail />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <AppLayout>
                      <ProtectedRoute>
                        <ReportList />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/stats"
                  element={
                    <AppLayout>
                      <ProtectedRoute managerOnly={true}>
                        <ReportStats />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/reports/create"
                  element={
                    <AppLayout>
                      <ProtectedRoute>
                        <CreateReport />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/reports/create/:excavatorId"
                  element={
                    <AppLayout>
                      <ProtectedRoute>
                        <CreateReport />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/reports/:id"
                  element={
                    <AppLayout>
                      <ProtectedRoute>
                        <ReportDetail />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/betriebstagebuch"
                  element={
                    <AppLayout>
                      <ProtectedRoute>
                        <BetriebstageBuchList />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/betriebstagebuch/create"
                  element={
                    <AppLayout>
                      <ProtectedRoute managerOnly={true}>
                        <BetriebstageBuchForm />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/betriebstagebuch/:id"
                  element={
                    <AppLayout>
                      <ProtectedRoute>
                        <BetriebstageBuchDetail />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/betriebstagebuch/:id/edit"
                  element={
                    <AppLayout>
                      <ProtectedRoute managerOnly={true}>
                        <BetriebstageBuchForm />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/inventory"
                  element={
                    <AppLayout>
                      <ProtectedRoute>
                        <Inventory />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <AppLayout>
                      <ProtectedRoute adminOnly={true}>
                        <UserManagement />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/betriebstagebuch/batch-create"
                  element={
                    <AppLayout>
                      <ProtectedRoute adminOnly={true}>
                        <BatchBetriebstagebuchForm />
                      </ProtectedRoute>
                    </AppLayout>
                  }
                />
                <Route
                  path="/404"
                  element={
                    <AppLayout>
                      <NotFound />
                    </AppLayout>
                  }
                />
                <Route path="*" element={<Navigate to="/404" replace />} />
              </Routes>
            </Router>
          </SidenavProvider>
        </LanguageProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
